import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useCallback, useState, type FunctionComponent } from 'react'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/loaders/[handler]'
import { getExcludedAllergens } from '../../const/AllergenHelper'
import { BANNER_INDEX } from '../../const/digitContants'
import { useTranslate } from '../../contexts/TranslationContext'
import type { ItemSubCategoryResult } from '../../data/ItemSubCategoryFragment'
import type { ProductBannerResult } from '../../data/ProductFragment'
import { useActiveDog, useDER } from '../../user/ActiveDogContext'
import { ProductBanner } from '../product/ProductBanner'
import { FoundProblemBanner } from '../static/FoundProblemBanner'
import { RegisterBanner } from '../static/RegisterBanner'
import { Hidden } from './Hidden'
import { InfoBar } from './InfoBar'
import style from './Listing.module.sass'
import { ShowMoreButton } from './ShowMoreButton'
import { SuggestionModal } from './SuggestionModal'

const api = createFetcher<API>('/api/loaders/[handler]')

export type ListingProductsProps = {
	products: (ProductBannerResult & { bestIndex?: number | boolean })[]
	allAllergens: ItemSubCategoryResult[]
	isResultPage?: boolean
	onNextPage: () => void
	hasNextPage: boolean
	totalCount: number
	greenProducts?: string[]
	redProducts?: string[]
	dogDailyEnergyRequirement?: number
	openModal: () => void
}

export const ListingProducts: FunctionComponent<ListingProductsProps> = ({
	products,
	allAllergens,
	onNextPage,
	hasNextPage,
	greenProducts,
	redProducts,
	isResultPage,
	openModal,
	dogDailyEnergyRequirement,
	totalCount = false,
}) => {
	const { dog: dogInfo } = useActiveDog()

	const isAllowedToFullUse = useIsAllowedToFullUse()

	const getDailyEnergyRequirement = useDER()
	const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false)

	const dogDailyEnergyRequirementNumber =
		dogDailyEnergyRequirement || Number(getDailyEnergyRequirement?.data?.dailyEnergyRequirement)

	const translate = useTranslate()

	const productPositionByDose = useQuery({
		queryKey: ['productPositions', dogInfo],
		queryFn: async () => {
			return dogInfo
				? api('productPositions', {
						dogInfo,
					})
				: null
		},
		enabled: Boolean(dogInfo),
	})

	const toggleSuggestionModal = useCallback((value: boolean) => setIsSuggestionModalOpen(value), [])

	const productsIdArray = productPositionByDose.data?.map((product) => product.id)
	return (
		<>
			<div className={style.bannerGroup}>
				{products.map((product, index) => (
					<div
						className={clsx(
							style.cardWrapper,
							(greenProducts?.includes(product.id) || redProducts?.includes(product.id)) && style.cardWrapperNoAfter,
							index === BANNER_INDEX &&
								products.length !== BANNER_INDEX + 1 &&
								!isAllowedToFullUse &&
								style.cardWrapperWithBanner,
						)}
						key={product.id}
					>
						{product && (
							<>
								<ProductBanner
									openModal={openModal}
									product={product}
									dogDailyEnergyRequirement={dogDailyEnergyRequirementNumber}
									isHighlighted={
										greenProducts?.includes(product.id)
											? 'green'
											: redProducts?.includes(product.id)
												? 'red'
												: undefined
									}
									position={(productsIdArray ?? []).indexOf(product.id)}
									excludedAllergens={getExcludedAllergens(allAllergens, product)}
									missingComposition={isResultPage && product.missingComposition}
									infoBarContent={
										product.missingComposition &&
										isResultPage && <InfoBar type="infoYellow" label={translate('product.alert.fediafNotApproved')} />
									}
								/>
							</>
						)}
						{isAllowedToFullUse
							? index === BANNER_INDEX &&
								products.length !== BANNER_INDEX + 1 && (
									<Hidden breakpoint="desktopUp">
										<FoundProblemBanner openModal={() => toggleSuggestionModal(true)} />
									</Hidden>
								)
							: index === BANNER_INDEX && products.length !== BANNER_INDEX + 1 && <RegisterBanner />}
					</div>
				))}
			</div>
			{hasNextPage && (
				<div className={style.buttonWrapper}>
					<ShowMoreButton handleClick={onNextPage} itemsAmount={Number(totalCount) - products.length} />
				</div>
			)}
			<SuggestionModal isModalOpen={isSuggestionModalOpen} handleClose={() => toggleSuggestionModal(false)} />
		</>
	)
}
